import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import restaurantStaffService from '../services/restaurantStaffService'
import { RESTAURANT_STAFF_ROLES, RestaurantStaffRole } from '../types/restaurantStaff'
import { useTranslation } from '../hooks/use-i18n'

interface InviteStaffModalProps {
  restaurantId: string
  onClose: () => void
  onSuccess: () => void
}

interface InviteForm {
  recipientEmail: string
  role: RestaurantStaffRole
}

const InviteStaffModal: React.FC<InviteStaffModalProps> = ({ restaurantId, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<InviteForm>({
    defaultValues: { role: 'waiter' },
  })

  const onSubmit = async (data: InviteForm) => {
    try {
      setIsLoading(true)
      await restaurantStaffService.invite(restaurantId, {
        recipientEmail: data.recipientEmail.trim(),
        role: data.role,
      })
      toast.success(t('toast.staffInvitationSent'))
      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.staffInvitationFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="blue-sky-card rounded-3xl p-8 w-full max-w-md mx-4 shadow-2xl animate-scale-in">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white">{t('staff.invite.title')}</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('staff.invite.emailLabel')}
            </label>
            <input
              {...register('recipientEmail', {
                required: t('auth.validation.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.validation.invalidEmail'),
                },
              })}
              type="email"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
              placeholder={t('staff.invite.emailPlaceholder')}
            />
            {errors.recipientEmail && (
              <p className="mt-2 text-sm text-red-400">{errors.recipientEmail.message}</p>
            )}
            <p className="mt-2 text-xs text-white/50">
              {t('staff.invite.emailHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('staff.invite.roleLabel')} <span className="text-red-400">{t('staff.invite.roleRequired')}</span>
            </label>
            <select
              {...register('role', { required: t('auth.validation.roleRequired') })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
            >
              {RESTAURANT_STAFF_ROLES.map((role) => (
                <option key={role.value} value={role.value} className="bg-[#2a2a2a]">
                  {role.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-2 text-sm text-red-400">{errors.role.message}</p>
            )}
          </div>

          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-white/30 text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-semibold"
            >
              {t('staff.invite.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl font-semibold"
            >
              {isLoading ? t('staff.invite.submitting') : t('staff.invite.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InviteStaffModal
